/**
 * The dossier card (08 §2.4): insignia, name and callsign, status chip, merit bar to the next
 * rank, ribbon rack, perks, traits, wound and fatigue state, service record. Unavailable
 * rangers are greyed and say why. A card is a view; the screen adds the buttons that act on it.
 */

import './dossier.css';
import './insignia.css';
import type { RangerRecord } from '../../../meta/types';
import { box, el } from '../dom';
import type { Describe } from '../text';
import { availability, fatiguePenaltyPercent, fullName, meritProgress, rankAbbr, safeDescribe } from '../text';
import { progressBar } from './gauge';
import { rankInsignia } from './insignia';
import { ribbonRack } from './ribbonRack';
import { stamp } from './stamp';

export interface DossierOptions {
  describe: Describe;
  /** Name, chip, trait and record only: the company rail and the depot candidates. */
  compact?: boolean;
  /** Two-tone, for the Memorial Wall. */
  memorial?: boolean;
}

/** A head-and-shoulders silhouette in CSS; the seed picks one of three skin ramps (05 §4.2). */
export function bust(seed: number, twoTone = false): HTMLElement {
  const tone = Math.abs(Math.trunc(seed)) % 3;
  const root = el('div', twoTone ? 'menu-bust menu-bust-memorial' : `menu-bust menu-bust-${tone}`);
  root.setAttribute('aria-hidden', 'true');
  root.append(el('span', 'menu-bust-helmet'), el('span', 'menu-bust-face'), el('span', 'menu-bust-body'));
  return root;
}

function tagList(title: string, ids: readonly string[], describe: Describe, empty: string): HTMLElement {
  const row = el('div', 'menu-dossier-tags');
  row.append(el('span', 'menu-dossier-key', title));
  if (ids.length === 0) row.append(el('span', 'menu-dim', empty));
  for (const id of ids) {
    const d = safeDescribe(describe, id);
    const tag = el('span', 'menu-tag', d.name);
    tag.title = d.text;
    row.append(tag);
  }
  return row;
}

function recordLine(r: RangerRecord): HTMLElement {
  const rec = r.record;
  const cells: Array<[string, number]> = [
    ['MSN', rec.missions],
    ['OK', rec.successes],
    ['KILLS', rec.kills],
    ['KNIFE', rec.knifeKills],
    ['WND', rec.woundsTaken],
    ['POW', rec.timesCaptured],
  ];
  const row = el('div', 'menu-dossier-record');
  for (const [key, value] of cells) {
    row.append(box('menu-dossier-cell', el('span', 'menu-dossier-key', key), el('span', 'menu-paper', String(value))));
  }
  return row;
}

function stateLine(r: RangerRecord): HTMLElement {
  const row = el('div', 'menu-dossier-state');
  const penalty = fatiguePenaltyPercent(r.fatigue);
  const fatigue = el('span', penalty > 0 ? 'menu-warn' : 'menu-dim');
  fatigue.textContent = penalty > 0 ? `FATIGUE ${r.fatigue}: STAMINA -${penalty}%` : `FATIGUE ${r.fatigue}`;
  row.append(fatigue);
  if (r.recoveryMissions > 0) {
    row.append(el('span', 'menu-bad', `RECOVERY ${r.recoveryMissions}`));
  }
  if (r.pendingPerkChoices.length > 0) row.append(el('span', 'menu-good', 'PERK OWED'));
  return row;
}

export function dossierCard(r: RangerRecord, options: DossierOptions): HTMLElement {
  const state = availability(r);
  const card = el('div', 'menu-dossier');
  card.classList.toggle('menu-dossier-out', !state.available && options.memorial !== true);
  card.classList.toggle('menu-dossier-compact', options.compact === true);
  card.classList.toggle('menu-dossier-memorial', options.memorial === true);

  const names = el('div', 'menu-dossier-names');
  names.append(
    el('div', 'menu-dossier-name', `${rankAbbr(r.rank)} ${fullName(r)}`),
    el('div', 'menu-dossier-callsign', r.callsign.trim() === '' ? '-' : `"${r.callsign}"`),
  );
  const head = el('div', 'menu-dossier-head');
  head.append(rankInsignia(r.rank), bust(r.seed, options.memorial === true), names);
  if (options.memorial !== true) head.append(el('span', `menu-chip menu-chip-${state.tone}`, state.chip));
  card.append(head);
  if (r.veteran) card.append(stamp({ text: 'VETERAN', tone: 'green' }, true));

  if (options.compact === true) {
    card.append(tagList('TRAITS', r.traits, options.describe, 'NONE'));
    return card;
  }

  const merit = meritProgress(r.rank, r.merit);
  const meritRow = el('div', 'menu-dossier-merit');
  const meritText = merit.next === null
    ? `MERIT ${r.merit.toLocaleString('en-US')}`
    : `MERIT ${merit.into.toLocaleString('en-US')}/${merit.span.toLocaleString('en-US')} TO ${merit.next}`;
  meritRow.append(el('span', 'menu-dossier-key', meritText), progressBar(merit.fraction));
  card.append(
    meritRow,
    ribbonRack(r.medals, options.describe),
    tagList('PERKS', r.perks, options.describe, 'NONE YET'),
    tagList('TRAITS', r.traits, options.describe, 'NONE'),
  );
  if (options.memorial !== true) card.append(stateLine(r));
  card.append(recordLine(r));
  if (!state.available && options.memorial !== true) card.append(el('div', 'menu-dossier-reason', state.reason));
  return card;
}
